import Handlebars from '@sparta.global.vendors/handlebars';
import { button } from '@sparta.global.helpers/button-helper/dist/es5.js';

/* global Handlebars */

/**
 * @const STRINGS
 * @description constant for storing message strings
 */
var STRINGS = {
  NO_ACCORDION_CONTENT_ERROR: 'No squeezebox JCR objects provided to helper-squeezebox.',
  NO_PANEL_CONTENT_ERROR: '<strong>Error:</strong> No panel content available.'
};

/**
 * Loops through the JCR contexts and matches the context for the current Panel content, then
 * compiles and returns the content into a Handlebars template string.
 * @param {object} panel - Object of JCR parameters for the current Panel.
 * @param {object} contexts - JCR contexts provided by the Handlebars template.
 * @param {function} template - Handlebars template used to compile the Panel content.
 * @returns {string} Compiled Handlebars template string with dynamic Panel content.
 */
function getDynamicContent(panel, contexts, template) {
  var panelContent;
  var panelContext;
  if (panel.dynamicContent === 'partial' && panel.content instanceof Object) {
    panelContent = template(panel.content);
  } else if (contexts.length > 0) {
    // Check to see if the value provided within panel.content matches the JCR path.
    contexts.forEach(function (context) {
      if (panel.content === context.jcr) {
        panelContext = context;
      }
    });

    // If path for the partial context is missing output an error message.
    if (!panelContext) {
      panelContent = STRINGS.NO_PANEL_CONTENT_ERROR;
    } else if (panel.dynamicContent === 'partial') {
      panelContent = template(panelContext);
    } else if (panel.dynamicContent === 'markdown') {
      panelContent = template({
        content: panelContext
      });
    } else {
      panelContent = panel.content;
    }
  } else {
    panelContent = STRINGS.NO_PANEL_CONTENT_ERROR;
  }
  return panelContent;
}

/**
   * @method helpers.squeezebox
   * @param {object} partials Object of partials coming from module JCR.
   * @param {object} jcr JSON object used to build the squeezebox.
   * @returns {Node} `Handlebars.SafeString` version of `squeezeboxWrap` element.
   * @description This pieces together all the HTML elements needed to make an squeezebox.
   */
function squeezebox(partials, jcr) {
  var _jcr$options;
  if (!jcr) {
    throw new Error(STRINGS.NO_ACCORDION_CONTENT_ERROR);
  }

  // Combine all the additional arguments into an array of contexts for the partials/Markdown
  // content files.
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }
  var jcrContexts = Array.prototype.slice.call(args, 0, -1);

  // Set defaults if JCR options do not exist.
  var oneAtATime = jcr.options && jcr.options.oneAtATime || '';
  var expandCollapseLink = jcr.options && jcr.options.expandCollapseLink || 'auto';

  // Enable collapse of inner panels via JCR.
  var expandCollapseInner = jcr.options && jcr.options.expandCollapseInnerPanels === true ? 'data-expand-collapse-inner-panels="true"' : '';

  // When paired with print module, check if squeezebox needs to open or remain in current state
  var printShowAll = (jcr === null || jcr === void 0 || (_jcr$options = jcr.options) === null || _jcr$options === void 0 || (_jcr$options = _jcr$options.printShowAll) === null || _jcr$options === void 0 ? void 0 : _jcr$options.toString()) === 'false' ? 'data-print-show-all="false"' : 'data-print-show-all="true"';

  // Expand and Collapse link text
  var expandLinkText = jcr.expandLinkText || 'Expand all';
  var collapseLinkText = jcr.collapseLinkText || 'Collapse all';
  var expandLinkBactmln = jcr.expandLinkBactmln || null;
  var collapseLinkBactmln = jcr.collapseLinkBactmln || null;
  var adaLabel = jcr.adaCaption ? "aria-label='".concat(jcr.adaCaption, "'") : '';
  var squeezeBoxType = jcr.type || '';

  // Spinner for dynamic content.
  var spinner = jcr.spinner === true ? 'data-spinner="true"' : '';
  var spinnerText = jcr.spinnerText ? "data-spinner-text=\"".concat(jcr.spinnerText, "\"") : '';

  // Overall wrapper.
  var squeezeboxWrap = "\n  <section\n    class=\"squeezebox-wrapper ".concat(jcr.class ? jcr.class : '', "\"\n    id=\"").concat(jcr.id, "\"\n    data-one-at-a-time=\"").concat(oneAtATime, "\"\n    data-squeezebox-type=\"").concat(squeezeBoxType, "\"\n    ").concat(expandCollapseInner, "\n    ").concat(adaLabel, "\n  >\n");

  // Optional squeezebox title.
  var titleHeadingTag = jcr.titleHeadingTag || 'h3';
  var squeezeboxTitle = jcr.title ? "<".concat(titleHeadingTag, ">").concat(jcr.title, "</").concat(titleHeadingTag, ">") : '';

  // Hidden `break-out` link used to support display of all data for SEO.
  var breakoutLink = "\n  <a\n    href=\"#\"\n    class=\"spa-hidden spa-prevent-default-click\"\n    aria-hidden=\"true\"\n    data-breakout\n  >Expand all panels</a>\n";

  // Include the `break-out` links for all squeezebox groups.
  var squeezeboxGroupControls = "".concat(breakoutLink);

  // If the squeezebox group is configured with visible expand/collapse all links, add them after
  // the `break-out` link.
  if (oneAtATime !== 'true' && expandCollapseLink === 'visible') {
    var groupLinkWrapper = "\n    <div class=\"squeezebox-group-control-wrapper\" data-font=\"cnx-regular\">\n  ";
    var expandLink = "\n    <button\n      class=\"squeezebox-group-control squeezebox-expand-all-link spa-btn spa-btn--link\"\n      type=\"button\"\n      ".concat(expandLinkBactmln ? "data-bactmln=\"".concat(expandLinkBactmln, "\"") : '', "\n    >").concat(expandLinkText, "<span class=\"ada-hidden\"> panels</span></button>\n  ");
    var linkDivider = '<span class="squeezebox-link-divider"> | </span>';
    var collapseLink = "\n    <button\n      class=\"squeezebox-group-control squeezebox-collapse-all-link spa-btn spa-btn--link\"\n      type=\"button\"\n      ".concat(collapseLinkBactmln ? "data-bactmln=\"".concat(collapseLinkBactmln, "\"") : '', "\n    >").concat(collapseLinkText, "<span class=\"ada-hidden\"> panels</span></button>\n  ");
    squeezeboxGroupControls += "".concat(groupLinkWrapper).concat(expandLink).concat(linkDivider).concat(collapseLink, "</div>");
  }

  // Optional header colors.
  var headerColor = jcr.options && jcr.options.color || '';
  var hasIcon = jcr.hasIcon ? 'spa-has-icon' : '';

  // This will hold our panel content and small screen panel headers.
  var squeezeboxPanels = "<div class=\"squeezebox-panels ".concat(hasIcon, "\">");

  // Append each panel instance to above elements.
  jcr.panels.forEach(function (panel, index) {
    // Apply `active` class if present. Defaults to '' if not set in JCR.
    var activeClass = panel.active ? 'active' : '';
    var panelID = "".concat(jcr.id, "Panel").concat(index);
    var panelSlug = panel.slug ? panel.slug : panelID;
    var panelCustomClass = panel.class ? panel.class : '';
    var hiddenAttr = !panel.active ? 'hidden' : '';
    var preserveFlag = panel.preserveContent === true ? 'data-preserve="true"' : '';
    preserveFlag = panel.preserveContent === false ? 'data-preserve="false"' : preserveFlag;
    var panelDataAttributes = '';
    var panelColor = panel.panelColor || '';
    var panelHeaderColor = panelColor || headerColor;
    var icon = jcr.hasIcon ? '<span class="spa-squeezebox-icon"></span>' : '';
    var subheadingText = panel.subheading ? "<span class=\"spa-squeezebox-subheading\">".concat(panel.subheading, "</span>") : '';
    if (panel.data) {
      Object.keys(panel.data).forEach(function (attribute) {
        panelDataAttributes += " data-".concat(attribute, "=\"").concat(panel.data[attribute], "\"");
      });
    }

    // Build each panel header.
    var panelHeader = "\n    <button\n      name=\"".concat(panel.label.replace(/[^a-zA-Z ]/g, '').replace(/ /g, '_'), "\"\n      class=\"panel-header spa-oline ").concat(panelHeaderColor, " spa-btn spa-btn--link\"\n      data-panel=\"").concat(panelID, "\"\n      ").concat(panelDataAttributes, "\n      type=\"button\"\n      aria-controls=\"").concat(panelID, "\"\n      ").concat(printShowAll, "\n    >\n      ").concat(icon, "<span class=\"spa-squeezebox-arrow spa-icon icon-chevron-up\"></span>").concat(panel.label, "\n      <span class=\"ada-hidden\">").concat(panel.adaPanelCaption || '', "</span>\n      ").concat(subheadingText, "\n    </button>\n  ");

    // Build each panel content.
    // TODO: I have broken out the content element because I was hoping to inject a partial HBS
    // file into `contentMid`.
    var contentStart = "<div class=\"panel-content ".concat(headerColor, "\" ").concat(hiddenAttr, ">");
    var contentMid = '';
    var contentEnd = '</div>';

    // TODO: Inner Panels loop. This should really be a recursive call of this same function. If
    // there is an inner squeezebox, It basically builds the same HTML as above, but adds a few
    // inner options or classes.
    if (typeof panel.innerPanels !== 'undefined') {
      // Set defaults if JCR options do not exist for this panel.
      var innerOneAtATime = panel.innerPanels.oneAtATime ? panel.innerPanels.oneAtATime : '';
      var innerExpandCollapseLink = panel.innerPanels.expandCollapseLink ? panel.innerPanels.expandCollapseLink : 'auto';
      var innerID = panel.innerPanels.id ? panel.innerPanels.id : "".concat(jcr.id, "Child").concat(index);
      contentMid = "\n      <div class=\"content\">\n        <div class=\"row\">\n          <div class=\"column\">\n            <section\n              class=\"squeezebox-wrapper innerPanels\"\n              id=\"".concat(innerID, "\"\n              data-one-at-a-time=\"").concat(innerOneAtATime, "\"\n            >\n    ");
      var innerTitleHeadingTag = panel.innerPanels.titleHeadingTag ? panel.innerPanels.titleHeadingTag : 'h4';
      var innerTitle = panel.innerPanels.title ? "<".concat(innerTitleHeadingTag, ">").concat(panel.innerPanels.title, "</").concat(innerTitleHeadingTag, ">") : '';

      // Hidden `break-out` link used to support display of all data for SEO.
      var innerBreakoutLink = "\n      <a\n        href=\"#\"\n        class=\"spa-hidden break-out spa-prevent-default-click\"\n        aria-hidden=\"true\"\n        data-breakout-link=\"".concat(innerID, "\"\n      >Expand all panels</a>\n    ");

      // Include the `break-out` links for all squeezebox groups.
      var innerSqueezeboxGroupControls = "".concat(innerBreakoutLink);

      // If the inner squeezebox group is configured with visible expand/collapse all links, add
      // them after the `break-out` link
      var innerExpandButtonData = {
        content: "".concat(expandLinkText),
        adaText: "panels",
        classes: "squeezebox-group-control spa-prevent-default-click squeezebox-expand-all-link spa-btn spa-btn--link",
        attributes: {
          id: "innerExpandButton"
        }
      };
      var innerCollapseButtonData = {
        content: "".concat(collapseLinkText),
        adaText: "panels",
        classes: "squeezebox-group-control spa-prevent-default-click squeezebox-collapse-all-link spa-btn spa-btn--link",
        attributes: {
          id: "innerCollapseButton"
        }
      };
      if (innerOneAtATime !== 'true' && innerExpandCollapseLink === 'visible') {
        var innerGroupLinkWrapper = "\n        <div class=\"squeezebox-group-control-wrapper\" data-font=\"cnx-regular\">\n      ";
        var innerExpandLink = "".concat(button(innerExpandButtonData));
        var innerLinkDivider = '<span class="squeezebox-link-divider"> | </span>';
        var innerCollapseLink = "".concat(button(innerCollapseButtonData));
        innerSqueezeboxGroupControls += "\n          ".concat(innerGroupLinkWrapper).concat(innerExpandLink).concat(innerLinkDivider).concat(innerCollapseLink, "\n        </div>\n      ");
      }
      var innerColor = panel.innerPanels.color ? panel.innerPanels.color : '';
      var _hasIcon = panel.innerPanels.hasIcon ? 'spa-has-icon' : '';
      var innerPanels = "<div class=\"squeezebox-panels ".concat(_hasIcon, "\">");
      panel.content.forEach(function (innerPanel, innerIndex) {
        var innerPanelID = "".concat(innerID, "Panel").concat(innerIndex);
        var innerPanelSlug = innerPanel.slug ? innerPanel.slug : innerPanelID;
        var innerPanelDataAttributes = '';
        var innerActiveClass = innerPanel.active ? 'active' : '';
        var icon = panel.innerPanels.hasIcon ? '<span class="spa-squeezebox-icon"></span>' : '';
        var subheadingText = innerPanel.subheading ? "<span class=\"spa-squeezebox-subheading\">".concat(innerPanel.subheading, "</span>") : '';
        if (innerPanel.data) {
          Object.keys(innerPanel.data).forEach(function (attribute) {
            innerPanelDataAttributes += " data-".concat(attribute, "=\"").concat(innerPanel.data[attribute], "\"");
          });
        }

        // Account for introductory text before the nested panels.
        if (typeof innerPanel === 'string') {
          innerPanels += innerPanel;
          return;
        }
        var innerHeader = "\n        <button\n          class=\"panel-header spa-oline ".concat(innerColor, " spa-btn spa-btn--link\"\n          data-panel=\"").concat(innerPanelID, "\"\n          ").concat(innerPanelDataAttributes, "\n          type=\"button\"\n          data-breakout\n        >\n          ").concat(icon, "<span\n            class=\"spa-squeezebox-arrow spa-icon\"\n            data-breakout\n          ></span>").concat(innerPanel.label, "\n          <span class=\"ada-hidden\">").concat(innerPanel.adaPanelCaption || '', "</span>\n          ").concat(subheadingText, "\n        </button>\n      ");
        var innerContent;
        var startingHTML = "\n        <div\n          class=\"panel-content panel-content--nested ".concat(innerColor, "\"\n        >\n      ");
        var endingHTML = '</div></div></div>';
        if (innerPanel.dynamicContent === 'partial') {
          // Partials will be loaded from the default locations for Handlebars templates
          // (e.g. templates, templates/partials from the module folder)
          /*
          const templateString = `
            ${startingHTML}
            <div class="row collapse">
              <div class="column">
                {{> ${innerPanel.partialName}}}
            ${endingHTML}
          `;
          */
          var partialTemplate = partials[innerPanel.partialName];
          innerContent = getDynamicContent(innerPanel, jcrContexts, partialTemplate);
        } else if (innerPanel.dynamicContent === 'markdown') {
          var templateString = "\n          ".concat(startingHTML, "\n          <div class=\"row collapse\">\n            <div class=\"column\">\n              {{{content.markdown}}}\n          ").concat(endingHTML, "\n        ");
          var _partialTemplate = Handlebars.compile(templateString);
          innerContent = getDynamicContent(innerPanel, jcrContexts, _partialTemplate);
        } else if (innerPanel.dynamicContent === 'module') {
          if (jcrContexts.length !== 0 && innerPanel.moduleId) {
            var moduleContent = null;
            jcrContexts.every(function (context) {
              if (Object.prototype.hasOwnProperty.call(context, innerPanel.moduleId)) {
                moduleContent = context[innerPanel.moduleId];
                return false;
              }
              return true;
            });
            if (moduleContent === null) {
              contentMid = "\n              ".concat(startingHTML, "\n              <div class=\"content\">\n                <div class=\"row collapse\">\n                  <div class=\"column\">").concat(STRINGS.NO_PANEL_CONTENT_ERROR, "</div>\n              ").concat(endingHTML, "\n            ");
            } else {
              contentMid = "\n              ".concat(startingHTML, "\n              <div class=\"content\">\n                <div class=\"row collapse\">\n                  <div class=\"column\">").concat(moduleContent, "</div>\n              ").concat(endingHTML, "\n            ");
            }
          } else if (innerPanel.moduleName) {
            contentMid = "\n            ".concat(startingHTML, "\n            <div class=\"content\">\n              <div class=\"row collapse\">\n                <div class=\"column\">\n                  <div data-panel-content=\"").concat(innerPanel.moduleName, "\"></div>\n                </div>\n            ").concat(endingHTML, "\n          ");
          } else {
            contentMid = "\n            ".concat(startingHTML, "\n            <div class=\"content\">\n              <div class=\"row collapse\">\n                <div class=\"column\">").concat(STRINGS.NO_PANEL_CONTENT_ERROR, "</div>\n            ").concat(endingHTML, "\n          ");
          }
        } else {
          innerContent = "\n          ".concat(startingHTML, "\n          <div class=\"content\">\n            <div class=\"row collapse\">\n              <div class=\"column\">").concat(innerPanel.content, "</div>\n          ").concat(endingHTML, "\n        ");
        }

        // Only set status class if status icon included
        var panelHeaderStatus = jcr.hasIcon && innerPanel.status ? "spa-squeezebox-panel--".concat(innerPanel.status) : '';
        innerPanels += "\n        <div\n          class=\"squeezebox-panel ".concat(innerActiveClass, " ").concat(panelHeaderStatus, " spa-inner-panel\"\n          id=\"").concat(innerPanelID, "\"\n          data-slug=\"").concat(innerPanelSlug, "\"\n        >").concat(innerHeader).concat(innerContent, "</div>\n      ");
      });
      contentMid += "\n                  ".concat(innerTitle).concat(innerSqueezeboxGroupControls, "\n                ").concat(innerPanels, "\n              </div>\n            </section>\n          </div>\n        </div>\n      </div>\n    ");
    } else if (panel.dynamicContent === 'partial') {
      // Partials will be loaded from the default locations for Handlebars templates
      // (e.g. templates, templates/partials from the module folder)
      /*
      const templateString = `
        <div class="content">
          <div class="row">
            <div class="column">{{> ${panel.partialName}}}</div>
          </div>
        </div>
      `;
      */
      var partialTemplate = partials[panel.partialName];
      contentMid = getDynamicContent(panel, jcrContexts, partialTemplate);
    } else if (panel.dynamicContent === 'markdown') {
      var templateString = "\n      <div class=\"content\">\n        <div class=\"row\">\n          <div class=\"column\">{{{content.markdown}}}</div>\n        </div>\n      </div>\n    ";
      var _partialTemplate2 = Handlebars.compile(templateString);
      contentMid = getDynamicContent(panel, jcrContexts, _partialTemplate2);
    } else if (panel.dynamicContent === 'module') {
      if (jcrContexts.length !== 0 && panel.moduleId) {
        var moduleContent = null;
        jcrContexts.every(function (context) {
          if (Object.prototype.hasOwnProperty.call(context, panel.moduleId)) {
            moduleContent = context[panel.moduleId];
            return false;
          }
          return true;
        });
        if (moduleContent === null) {
          contentMid = "\n          <div class=\"content\">\n            <div class=\"row\">\n              <div class=\"column\">".concat(STRINGS.NO_PANEL_CONTENT_ERROR, "</div>\n            </div>\n          </div>\n        ");
        } else {
          contentMid = "\n          <div class=\"content\">\n            <div class=\"row collapse\">\n              <div class=\"column\">".concat(moduleContent, "</div>\n            </div>\n          </div>\n        ");
        }
      } else if (panel.moduleName) {
        contentMid = "\n        <div class=\"content\">\n          <div class=\"row collapse\">\n            <div class=\"column\">\n              <div data-panel-content=\"".concat(panel.moduleName, "\"></div>\n            </div>\n          </div>\n        </div>\n      ");
      } else {
        contentMid = "\n        <div class=\"content\">\n          <div class=\"row\">\n            <div class=\"column\">".concat(STRINGS.NO_PANEL_CONTENT_ERROR, "</div>\n          </div>\n        </div>\n      ");
      }
    } else {
      contentMid = Array.isArray(panel.content) ? panel.content.join('') : panel.content;
    }
    var panelContent = "".concat(contentStart).concat(contentMid).concat(contentEnd);
    // Only set status class if status icon included
    var panelHeaderStatus = jcr.hasIcon && panel.status ? "spa-squeezebox-panel--".concat(panel.status) : '';

    // Add each panel instance to a container.
    squeezeboxPanels += "\n    <div\n      class=\"squeezebox-panel ".concat(panelCustomClass, " ").concat(activeClass, " ").concat(panelHeaderStatus, "\"\n      id=\"").concat(panelID, "\"\n      data-slug=\"").concat(panelSlug, "\"\n      ").concat(preserveFlag, "\n      ").concat(spinner, "\n      ").concat(spinnerText, "\n    >").concat(panelHeader).concat(panelContent, "</div>\n  ");
  });

  // Compile all the squeezebox HTML.
  squeezeboxWrap += "\n        ".concat(squeezeboxTitle).concat(squeezeboxGroupControls, "\n      ").concat(squeezeboxPanels, "\n    </div>\n  </section>\n");
  return new Handlebars.SafeString(squeezeboxWrap);
}
squeezebox.functionName = "squeezebox";
/**
 * @class squeezeboxHelper
 * @description Class used to register our helper with Handlebars.
 */

export { squeezebox };